'use client';

import { useEffect } from 'react';
import type { EkomiWidgetRef } from './types';

const WIDGET_SCRIPT_SRC =
  'https://sw-assets.ekomiapps.de/static_resources/widget.js';
const WIDGET_SERVER_HOST = '//widgets.ekomi.com';

/**
 * Globals eKomi's loader reads. They are set on `window` rather than passed as
 * arguments because that is the only interface `widget.js` offers — it is the
 * same contract the legacy TYPO3 site uses.
 */
interface EkomiWindow extends Window {
  _ekomiWidgetsServerUrl?: string;
  _customerId?: string | number;
  _language?: string;
  _ekomiWidgetTokens?: string[];
  ekomiWidgetJs?: boolean;
}

/**
 * eKomi's customer rating badge, rendered entirely by eKomi's own script
 * (SH-403, spec 60.12).
 *
 * We contribute an empty container and the identifiers; `widget.js` fetches the
 * rating, paints the seal — including choosing its tier, which is not exposed
 * in any feed we can read — and publishes the `aggregateRating` structured
 * data. Deliberately **not** gated behind Usercentrics: the widget serves
 * general information about the organisation and links to nothing personal
 * unless the visitor taps it, and gating it would also suppress the structured
 * data, since eKomi owns that too.
 *
 * Renders nothing without both identifiers — a Standort with no eKomi account
 * shows no badge rather than borrowing the brand-wide rating, which would
 * attribute another business's review count to it.
 */
export function EkomiBadge({ customerId, widgetToken }: EkomiWidgetRef) {
  useEffect(() => {
    const w = window as EkomiWindow;

    w._ekomiWidgetsServerUrl = `${window.location.protocol}${WIDGET_SERVER_HOST}`;
    w._customerId = customerId;
    w._language = document.documentElement.lang || 'de';
    w._ekomiWidgetTokens = [
      ...(w._ekomiWidgetTokens ?? []).filter((t) => t !== widgetToken),
      widgetToken,
    ];

    // `widget.js` registers itself globally and scans the document once, so a
    // second copy would double-render every badge on the page. The guard is
    // eKomi's own, kept verbatim from their embed snippet.
    if (w.ekomiWidgetJs) {
      return;
    }
    w.ekomiWidgetJs = true;

    const script = document.createElement('script');
    script.src = WIDGET_SCRIPT_SRC;
    script.async = true;
    document.head.appendChild(script);
  }, [customerId, widgetToken]);

  // The spacing lives on the container eKomi itself manages, not on a wrapper
  // around it. Roughly two thirds of Standort accounts are flagged
  // `doHideWidget` upstream, and eKomi honours that by setting `display: none`
  // here — which takes this padding with it. A wrapper would survive and leave
  // 16px of blank space under the Google summary on all of those pages.
  //
  // Only below `md`: from there the badge sits *beside* the Google summary
  // rather than under it (SH-452), and the row's own gap does the separating.
  // A `display: none` element is not a flex item, so that gap likewise
  // disappears with the badge — the same property, preserved horizontally.
  return (
    <div
      id="widget-container"
      className={`ekomi-widget-container ekomi-widget-${widgetToken} ekomi-rating pt-4 md:pt-0`}
      data-testid="ekomi-badge"
    />
  );
}
